<template>
  <figure
    data-id="responsive-video"
    :class="['h-auto', getAspectRatio]"
  >
    <video
      :poster="poster ?? undefined"
      :width="width ?? undefined"
      :height="height ?? undefined"
      :autoplay="autoplay ?? undefined"
      :muted="muted ?? undefined"
      :loop="loop ?? undefined"
      :playsinline="playsinline ?? undefined"
      class="w-full h-full object-cover object-center"
    >
      <source
        :src="src"
        :type="`video/${getVideoExtension(src)}`"
      />
      <slot>
        {{ fallbackText || 'Your browser does not support the video tag.' }}
      </slot>
    </video>
  </figure>
</template>

<script lang="ts" setup>
/**
 * Usage Examples:
 * 
 * 1. Using the default fallback:
 * <ResponsiveVideo src="/path/to/video.mp4" />
 * 
 * 2. Using the fallbackText prop:
 * <ResponsiveVideo 
 *   src="/path/to/video.mp4" 
 *   fallbackText="Video playback is not supported in your browser."
 * />
 * 
 * 3. Using the slot for custom content:
 * <ResponsiveVideo src="/path/to/video.mp4">
 *   <div class="text-center p-4">
 *     <p class="text-red-500">Unable to play video</p>
 *     <a href="/path/to/video.mp4" class="underline">Download video</a>
 *   </div>
 * </ResponsiveVideo>
 */

import type { AspectRatio } from '../../composables/useAspectRatio';
import { useAspectRatio } from '../../composables/useAspectRatio';
import { PropType } from 'vue';

/**
 * Component props definition for ResponsiveVideo.
 *
 * @property {string} src - The source URL of the video.
 * @property {boolean|null} [autoplay=null] - Whether the video should start playing automatically.
 * @property {boolean|null} [loop=null] - Whether the video should loop after ending.
 * @property {boolean|null} [muted=null] - Whether the video should be muted by default.
 * @property {boolean|null} [playsinline=null] - Whether the video should play inline on mobile devices.
 * @property {string} [aspectRatio='auto'] - The aspect ratio of the video container.
 * @property {number|null} [width=null] - The width of the video in pixels.
 * @property {number|null} [height=null] - The height of the video in pixels.
 * @property {string|null} [fallbackText=null] - The fallback text to display if the video cannot be played.
 */
const props = defineProps({
  src: {
    type: String,
    required: true
  },
  poster: {
    type: String,
    default: null
  },
  autoplay: {
    type: Boolean,
    default: null
  },
  muted: {
    type: Boolean,
    default: null
  },
  loop: {
    type: Boolean,
    default: null
  },
  playsinline: {
    type: Boolean,
    default: null
  },
  aspectRatio: {
    type: String as PropType<AspectRatio>,
    default: 'auto'
  },
  width: {
    type: Number,
    default: null
  },
  height: {
    type: Number,
    default: null
  },
  fallbackText: {
    type: String,
    default: null
  }
});

// Supported video mime type extensions
const supportedVideoExtensions = [
  'mp4', 'webm', 'ogg', 'avi', 'mov', 'wmv', 'flv', 
  'm4v', 'mkv', '3gp', 'ogv', 'ts', 'm2ts'
] as const;

/**
 * Extracts the file extension from a video source URL and checks if it's a supported video type.
 * Returns the extension if supported, otherwise returns the original src.
 * 
 * @param src - The video source URL or file path.
 * @returns The file extension if supported, otherwise the original src.
 */
const getVideoExtension = (src: string): string => {
  const lastDotIndex = src.lastIndexOf('.');

  // If no extension is found, return the original src
  if (lastDotIndex === -1) {
    return src;
  }

  // Extract the extension and normalize to lowercase
  const extension = src.substring(lastDotIndex + 1).toLowerCase();

  // Check if the extension is in the list of supported video extensions
  if (supportedVideoExtensions.includes(extension as typeof supportedVideoExtensions[number])) {
    return extension;
  }

  // If not supported, return the original src
  return src;
};

const { getAspectRatio } = useAspectRatio(() => props.aspectRatio);
</script>